import { LiveblocksErrorBoundary, RoomProvider, subscribeLiveblocksAvailability } from "@configs/liveblocks.config"
import Spinner from "@legacy/core/components/Spinner"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { Provider as JotaiProvider } from "jotai"
import { ThemeProvider } from "next-themes"
import { useEffect, useRef, useState } from "react"
import { Helmet } from "react-helmet"

import useIsDesktop from "@hooks/useIsDesktop"
import useToastQueue from "@hooks/useToastQueue"
import useUser from "@hooks/useUser"

import { PreviewModeProvider } from "@particles/PreviewMode/PreviewMode.provider"

import { Toaster } from "@molecules/Toast/Toast"

import { BottomBar, SideBar, TopBar } from "@organisms"

import { themeNameBySchemeCode } from "@constants/mappings/themeNameBySchemeCode.mapping"

import styles from "./AppFrame.module.scss"
import { AppFrameProps } from "./AppFrame.types"
import AppFramePageContent from "./AppFramePageContent/AppFramePageContent"

const queryClient = new QueryClient()

function AppFrame(props: AppFrameProps) {
    const { children, navigationType = "main", pageId } = props
    const { user } = useUser()
    const { ToastQueueManager } = useToastQueue()

    const isDesktop = useIsDesktop()
    const roomId = user?.service_company?.slug && `${user.service_company.slug}-${pageId}`

    const [liveblocksAvailable, setLiveblocksAvailable] = useState(true)
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const reconnectAttemptsRef = useRef(0)
    const maxReconnectAttempts = 10 // Stop trying after 10 attempts (50 minutes)

    useEffect(() => {
        // Subscribe to Liveblocks availability changes
        const unsubscribe = subscribeLiveblocksAvailability((available) => {
            if (!available) {
                // Liveblocks became unavailable
                setLiveblocksAvailable(false)

                // Clear any existing timeout
                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current)
                }

                // Try to reconnect every 5 minutes (up to max attempts)
                if (reconnectAttemptsRef.current < maxReconnectAttempts) {
                    reconnectAttemptsRef.current += 1
                    reconnectTimeoutRef.current = setTimeout(
                        () => {
                            console.log(
                                `Attempting to reconnect to Liveblocks... (Attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`,
                            )
                            setLiveblocksAvailable(true)
                        },
                        5 * 60 * 1000, // 5 minutes
                    )
                } else {
                    console.warn("Max reconnection attempts reached. Liveblocks will remain disabled.")
                }
            } else {
                // Liveblocks is available again
                setLiveblocksAvailable(true)
                reconnectAttemptsRef.current = 0

                // Clear reconnect timeout if connection is restored
                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current)
                    reconnectTimeoutRef.current = null
                }
            }
        })

        return () => {
            unsubscribe()
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current)
            }
        }
    }, [maxReconnectAttempts])

    const shouldHideNavigation = navigationType === "none"
    const shouldHideBottomBar = navigationType === "settings" || navigationType === "none" || isDesktop

    const renderNavigation = () => {
        if (shouldHideNavigation || !isDesktop || !user) {
            return null
        } else if (navigationType === "settings") {
            return <SideBar.Settings />
        } else {
            return <SideBar.Main />
        }
    }

    const renderPageContent = () => {
        return (
            <>
                <Helmet>
                    {process.env.FONTAWESOME_KIT_ID && (
                        <script
                            src={`https://kit.fontawesome.com/${process.env.FONTAWESOME_KIT_ID}.js`}
                            crossOrigin="anonymous"
                        />
                    )}
                </Helmet>
                {renderNavigation()}
                <div className={styles.base}>
                    <main>
                        <div className={styles.page}>{children}</div>
                    </main>

                    {!shouldHideBottomBar && <BottomBar />}
                </div>
                <ToastQueueManager />
                <Toaster />
            </>
        )
    }

    if (user) {
        return (
            <ThemeProvider
                defaultTheme={themeNameBySchemeCode[user.color_mode] || "system"}
                disableTransitionOnChange={true}
                attribute="class"
                storageKey="roopairs_theme"
            >
                <QueryClientProvider client={queryClient}>
                    <JotaiProvider>
                        <PreviewModeProvider>
                            <TopBar.Provider>
                                {liveblocksAvailable && roomId ? (
                                    <LiveblocksErrorBoundary fallback={renderPageContent()}>
                                        <RoomProvider id={roomId} initialPresence={{ online: true }}>
                                            {renderPageContent()}
                                        </RoomProvider>
                                    </LiveblocksErrorBoundary>
                                ) : (
                                    renderPageContent()
                                )}
                            </TopBar.Provider>
                        </PreviewModeProvider>
                    </JotaiProvider>
                </QueryClientProvider>
            </ThemeProvider>
        )
    } else {
        return (
            <div className={styles.loading}>
                <Spinner />
            </div>
        )
    }
}

const Root = AppFrame
const PageContent = AppFramePageContent

export default { Root, PageContent }
